import { ExportFormat } from '@ecosystem/newron-client-lib';
import React from 'react';
import { ColumnHeaderForCL, ColumnHeaderDisplayNameForCL } from '@models/CategoryLeaderboard';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  dataExportInitiatedTrackingFunction,
  useDataExportTracking,
} from '@utils/page-analytics/model/DataExportTracking';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { useExportModal } from '@hooks/export/export-modal/UseExportModal';
import { EmptyTabsPage, EnabledExportPages, ViewType } from '@hooks/export/Model';
import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import { CategoryLeaderboardDoubleBarChartInsight } from '@components/collections/category-leaderboard-double-bar-chart/Model';
import CategoryLeaderboardCollectionButton from '../category-leaderboard-collection-button/CategoryLeaderboardCollectionButton';
import { WithExportModal } from '@components/design';
import { ToggleViewButtons } from '@components/toggle-view-buttons/ToggleViewButton';

export interface MarketCardHeaderIconProps {
  isTableView: boolean;
  onChartDownload: (exportFormats: ExportFormat[]) => Promise<void>;
  isExportButtonDisabled: boolean;
  volume: string;
  createInsightDataFn: CreateInsightDataFn<CategoryLeaderboardDoubleBarChartInsight>;
  onChange: (view: ViewType) => void;
}

const CategoryLeaderboardHeaderIcon: React.FC<MarketCardHeaderIconProps> = ({
  isTableView,
  onChartDownload,
  isExportButtonDisabled,
  volume,
  createInsightDataFn,
  onChange,
}) => {
  const pagesConfig = usePageConfig();

  const viewType = isTableView ? ViewType.Table : ViewType.BarChart;

  const {
    openExportModal,
    onCloseExportModal,
    onExportError,
    exportModalFormats,
    exportModalMessage,
    exportModalHeaderText,
    isExportModalOpen,
  } = useExportModal({
    currentPage: EnabledExportPages.CategoryLeaderboard,
    currentTab: EmptyTabsPage.None,
    currentView: viewType,
  });
  const handleDataExportTrackingFunction = useDataExportTracking('mainFiltersRO');

  const handleExportTracking = () => {
    handleDataExportTrackingFunction(
      {
        elementType: isTableView ? ElementType.TABLE : ElementType.BAR_CHART,
        elementKpi: ColumnHeaderDisplayNameForCL(volume)[pagesConfig.sortingOptions.headerKey as ColumnHeaderForCL],
        elementSort: pagesConfig.sortingOptions,
      },
      dataExportInitiatedTrackingFunction,
    );
  };

  const onExportClick = () => {
    handleExportTracking();
    openExportModal();
  };

  return (
    <span className="flex items-center gap-1">
      <CategoryLeaderboardCollectionButton
        isTableView={isTableView}
        isDisabled={isExportButtonDisabled}
        createInsightDataFn={createInsightDataFn}
      />
      <ToggleViewButtons {...{ view: viewType, isExportDisabled: isExportButtonDisabled, onExportClick, onChange }} />

      <WithExportModal
        isShown={isExportModalOpen}
        exportFormats={exportModalFormats}
        message={exportModalMessage}
        title={exportModalHeaderText}
        download={onChartDownload}
        onClose={onCloseExportModal}
        onError={onExportError}
      />
    </span>
  );
};

export default CategoryLeaderboardHeaderIcon;
